import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Flexbox, FlexboxColumn } from '../../../components/action/Flexbox';
import { Line } from '../../../components/action/Line';
import ToggleSwitch from '../../../components/action/ToggleSwitch';
import ReviewSort from '../../../components/Filters/ReviewSort';
import ReviewCard from '../../../components/Review/Card/ReviewCard';
import ReviewCardSkeleton from '../../../components/Review/Card/ReviewCardSkelton';
import ProjectDropdown from '../../../components/Review/ProjectDropdown';
import ExpandableCategoryList from '../../../components/Review/ProjectSelector/ExpandableCategoryList';
import Tags from '../../../components/Tags/Tags';
import ThemedText from '../../../components/theme/ThemedText';
import { apis, CHAINS } from '../../../constants';
import { useBackendApi } from '../../../helpers/backendHelper';
import { usePostSeenTracker } from '../../../hooks/usePostSeenTracker';
import { useGlobalState } from '../../../state/GlobalStateProvider';
import './Tabs.scss';

const Feed = () => {
  const ALL_PROJECTS = 'All Projects';
  const history = useNavigate();
  const location = useLocation(); // gets query string
  // Parse query string
  const queryParams = new URLSearchParams(location.search);
  const project = queryParams.get('project');

  const [{ protocolMapping }] = useGlobalState();
  const backendHelper = useBackendApi();

  const [mainFeed, setMainFeed] = useState([]);
  const [selectedProject, setSelectedProject] = useState();
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [listOpen, setListOpen] = useState(false);
  const [categoryData, setCategoryData] = useState({});
  const [feedFilter, setFeedFilter] = useState(false);
  const [sortBy, setSortBy] = useState(null);
  const [selectedTag, setSelectedTag] = useState();
  const [reviewTag, setReviewTag] = useState();

  const pagesLoaded = useRef(new Set());

  const loaderRef = useRef(null);
  const observerRef = useRef(null);

  const { getRefForPost, sendPostSeen } = usePostSeenTracker({
    sendSeenPosts: {
      url: apis.backend.postSeen(),
      fallback: backendHelper.reviews.postSeen,
    },
  });
  function resetPagination() {
    setMainFeed([]);
    setPage(1);
    setHasMore(true);
    pagesLoaded.current.clear();
  }
  const getMainFeed = useCallback(
    async (body = {}, currentPage = 1, reset = false) => {
      if (loading || pagesLoaded.current.has(currentPage)) return;

      setLoading(true);
      try {
        const response = await backendHelper.reviews.getFeed({
          filter: {
            ...body,
            pagination: {
              page: currentPage,
              limit: 10,
            },
          },
        });

        const fetchedFeed = response?.data || [];

        if (reset) {
          setMainFeed(fetchedFeed);
        } else {
          setMainFeed(prev => [...prev, ...fetchedFeed]);
        }

        pagesLoaded.current.add(currentPage);

        if (fetchedFeed.length === 0) {
          setHasMore(false);
        }
      } catch (err) {
        console.error('Error fetching feed:', err);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    },
    [backendHelper, loading],
  );

  async function getReviewedReviewTag(protocol) {
    try {
      setReviewTag(undefined);
      const response = await backendHelper.reviews.getReviewTagsWithReviews(protocol);
      setReviewTag(response.data);
    } catch (error) {
      console.log('error is getting reviewTags', error);
    }
  }

  const handleChange = option => {
    if (option) {
      if (option?.protocol === selectedProject?.value) {
        history('/takes');
        setSelectedProject(null);
        setReviewTag(null);
      } else {
        setSelectedProject({ name: option?.name, value: option?.protocol });

        getReviewedReviewTag(option?.protocol);
      }
    } else {
      setSelectedProject(null);
      history('/takes/feed');
    }

    setListOpen(false);

    // Reset
    resetPagination();
  };

  function handleFilter(value) {
    setFeedFilter(value);

    //reset
    resetPagination();
  }

  function handleSort(value) {
    setSortBy(value);
    resetPagination();
  }

  function handleProtocol(item) {
    if (String(item?.protocol).startsWith('chain')) {
      return {
        ...item,
        ...(CHAINS[String(item?.protocol).split('-')[1]] || {}),
      };
    }
    return {
      ...item,
      ...(protocolMapping[item?.protocol] || {}),
    };
  }

  async function getCategoryData() {
    try {
      const data = await backendHelper.reviews.classifiedProtocolRating();
      const rawData = data.data; // assuming an object like { chains: [...], dex: [...] }

      // Transform the data: for each category, map each item through handleProtocol.
      const transformedData = Object.entries(rawData).reduce((acc, [category, items]) => {
        acc[category] = items.map(item => handleProtocol(item));
        return acc;
      }, {});

      setCategoryData(transformedData);
    } catch (error) {}
  }

  // function handleRedirect() {
  //   history(`/takes/${JSON.stringify(selectedProject)}`);
  //   handleTab(1);
  // }

  function handleSelectTag(value) {
    setSelectedTag(value);
    resetPagination();
  }

  useEffect(() => {
    if (project) {
      setMainFeed([]);
      setPage(1);
      setHasMore(true);
      pagesLoaded.current.clear();
    }
  }, []);

  useEffect(() => {
    let body = {};
    //if project in url
    if (project && !selectedProject) {
      resetPagination();
      getReviewedReviewTag(project);
      const rawProject = handleProtocol({ protocol: project });
      setSelectedProject({
        name: rawProject?.name,
        value: rawProject?.protocol,
      });
      body = {
        protocol: project,
      };
    }
    // selectedProject
    if (selectedProject) {
      body = { ...body, protocol: selectedProject.value };
    }
    // filteredBy
    if (feedFilter) {
      body = { ...body, superTakesOnly: Boolean(feedFilter) };
    }
    //sortBy
    if (sortBy) {
      body = {
        ...body,
        sort: {
          field: sortBy,
          order: 'desc',
        },
      };
    }
    if (selectedTag) {
      body = {
        ...body,
        tags: [selectedTag?.id],
      };
    }

    getMainFeed(body, page, page === 1);
  }, [page, selectedProject, getMainFeed, project, feedFilter, sortBy, selectedTag]);

  useEffect(() => {
    return () => {
      sendPostSeen();
    };
  }, []);

  useEffect(() => {
    if (!protocolMapping) return;
    getCategoryData();
  }, [protocolMapping]);

  // Intersection Observer Setup
  useEffect(() => {
    if (loading || !hasMore) return;

    const currentLoader = loaderRef.current;
    if (!currentLoader) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          setPage(prev => prev + 1);
        }
      },
      {
        rootMargin: '600px',
      },
    );

    observer.observe(currentLoader);
    observerRef.current = observer;

    return () => {
      if (observerRef.current && currentLoader) {
        observerRef.current.unobserve(currentLoader);
      }
    };
  }, [loading, hasMore]);

  return (
    <FlexboxColumn className='feed_wrapper feed_border' gap='1.5rem' justifyContent={'flex-start'}>
      <Flexbox fw justifyContent='space-between'>
        <Flexbox>
          <ReviewSort onChange={handleSort} filter={sortBy} />
          <ProjectDropdown placeholder={ALL_PROJECTS} selected={selectedProject} handleClick={() => setListOpen(!listOpen)} handleSelected={handleChange} />
        </Flexbox>

        <Flexbox gap='0.5rem' justifyContent='flex-start'>
          <ThemedText.H6 color='color-xlight' weight='semi-bold'>
            HeavyUser
          </ThemedText.H6>
          <ToggleSwitch value={feedFilter} onChange={handleFilter} />
        </Flexbox>
      </Flexbox>
      {listOpen && <ExpandableCategoryList data={categoryData} handleClick={handleChange} selected={selectedProject} />}
      {selectedProject && <Tags tags={[...(reviewTag?.['all'] || [])]} isLoading={reviewTag === undefined} onClick={handleSelectTag} />}

      {mainFeed.length === 0 &&
        loading &&
        Array.from({ length: 4 }).map((_, i) => (
          <>
            <ReviewCardSkeleton key={i} />
            <Line />
          </>
        ))}

      {mainFeed.map(feed => (
        <>
          <ReviewCard key={`main_feed_${feed.postId}`} ref={getRefForPost(feed.postId)} data={feed} maxWidth={true} />
          <Line className='line' />
        </>
      ))}

      {loading && <ReviewCardSkeleton />}
      {!loading && hasMore && (
        <div ref={loaderRef} className=''>
          <span className='invite_wrapper_loader'></span>
        </div>
      )}
      {!hasMore && (
        <ThemedText.H6 style={{ padding: '1rem' }} color='color-xlight'>
          You’ve reached the end.
        </ThemedText.H6>
      )}
    </FlexboxColumn>
  );
};

export default Feed;
